import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import theme from "../../config/theme";
import { InputHandle, TextInputProps } from "../../types/public";

const TextInput = forwardRef<InputHandle, TextInputProps>(
  (
    {
      label,
      name,
      initialValue = "",
      value,
      placeholder,
      secureTextEntry,
      textAlign,
      RightIcon,
      LeftIcon,
      onSubmit,
      rightIconOnSubmit,
      leftIconOnSubmit,
      onChange,
      onBlur,
      helperText,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(initialValue);
    const textInputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const { theme: themeMode } = useSelector((state: any) => state.globalState);
    const mode = themeMode === "light" ? theme.light : theme.dark;

    useImperativeHandle(ref, () => ({
      getValue: () => inputValue,
      onFocus: () => textInputRef.current?.focus(),
      setValue: (newValue) => setInputValue(newValue),
    }));

    useEffect(() => {
      if (value !== undefined) {
        setInputValue(value);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      if (onChange) {
        onChange(e);
      }
    };

    const handleLeftIconClick = () => {
      if (leftIconOnSubmit) {
        leftIconOnSubmit();
      }
    };

    const handleRightIconClick = () => {
      if (rightIconOnSubmit) {
        rightIconOnSubmit();
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <div className="relative flex flex-col gap-2 w-full">
        <div className="relative flex items-center gap-2 w-full">
          <label
            className={`absolute right-3 top-0 z-10 transform px-1 text-xs text-mobasher-gray ${
              isFocused || inputValue
                ? "-translate-y-2"
                : "translate-y-3 text-sm hidden"
            }`}
            style={{
              backgroundColor: mode.backgroundColor.bgSecondary,
            }}
          >
            {label}
          </label>
          <div className="border-1 flex relative" style={{ width: "100%" }}>
            {LeftIcon && (
              <div
                className="absolute top-[20%] left-[2%]"
                onClick={handleLeftIconClick}
              >
                {LeftIcon}
              </div>
            )}

            {RightIcon && (
              <div
                className="absolute top-[20%]"
                style={{ right: "10px" }}
                onClick={handleRightIconClick}
              >
                {RightIcon}
              </div>
            )}
            <input
              name={name}
              className={`mx-1 mb-4 peer block w-full rounded-md border border-[#9d9d9d] py-2.5 px-10  focus:outline-none focus:bg-white focus:border-[#028d91] focus:shadow-[0_0px_4px_#03797d73] !text-right disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 !text-[12px] border-red-500`}
              ref={textInputRef}
              value={inputValue}
              placeholder={isFocused ? "" : placeholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              style={{
                flex: 1,
                textAlign: textAlign,
                backgroundColor: mode.backgroundColor.bgSecondary,
                color: mode.color.textPrimary,
              }}
              type={secureTextEntry ? "password" : "text"}
              onKeyDown={(e) => e.key === "Enter" && onSubmit && onSubmit()}
              disabled={disabled}
              {...props}
            />
          </div>
        </div>
        {helperText && (
          <p className="text-right w-full text-[9px] text-mobasher-red mt-1 absolute bottom-0 pl-1">
            * {helperText}
          </p>
        )}
      </div>
    );
  }
);

export default TextInput;
